import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AuthApiService } from './auth-api.service';
import { ThemeHelper } from '../helper/theme.helper';
import { Title } from '@angular/platform-browser';
import { updatePrimaryPalette } from '@primeuix/themes';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private _themeData = new BehaviorSubject<ThemeHelper>(new ThemeHelper());
    readonly themeData = this._themeData.asObservable();

    constructor(
        private authApiService: AuthApiService,
        private titleService: Title
    ) {
        this.getData();
    }

    public async getData(): Promise<any> {
        const themeApiData = await firstValueFrom(this.authApiService.getThemeData()).catch((error) => {
            return undefined;
        });

        const theme = new ThemeHelper();

        if (!themeApiData) {
            document.getElementById('missing-theme-container')?.classList.remove('d-none');

            theme.theme_available = false;
            this._themeData.next(theme);
        } else {
            theme.theme_accent_color = themeApiData.find((x: any) => x.key === 'theme_accent_color')?.value || '#ff0000';
            theme.theme_dark_accent_color = themeApiData.find((x: any) => x.key === 'theme_dark_accent_color')?.value || '#ff0000';
            theme.theme_dark_mode = themeApiData.find((x: any) => x.key === 'dark_mode')?.valueBoolean || false;
            theme.theme_title = themeApiData.find((x: any) => x.key === 'theme_title')?.value || 'Betriebsdashboard VZ-NOW';
            theme.theme_logo = themeApiData.find((x: any) => x.key === 'theme_logo')?.value || '';
            theme.theme_logo_dark = themeApiData.find((x: any) => x.key === 'theme_logo_dark')?.value || '';
            theme.theme_slogan = themeApiData.find((x: any) => x.key === 'theme_slogan')?.value || 'Betriebsdashboard';
            theme.theme_favicon = themeApiData.find((x: any) => x.key === 'theme_favicon')?.value || '';
            theme.theme_motd_visible = themeApiData.find((x: any) => x.key === 'theme_motd_visible')?.valueBoolean || false;
            theme.theme_motd_text = themeApiData.find((x: any) => x.key === 'theme_motd_text')?.value || '';
            theme.theme_motd_color = themeApiData.find((x: any) => x.key === 'theme_motd_color')?.value || '#ff0000';
            theme.theme_staging = themeApiData.find((x: any) => x.key === 'theme_staging')?.valueBoolean || false;
            theme.theme_available = true;
            this.titleService.setTitle(theme.theme_title);
            this._themeData.next(theme);

            const colors = this.generateDarkerColors(theme.theme_accent_color);

            document.documentElement.style.setProperty('--theme-accent-color', theme.theme_accent_color);
            document.documentElement.style.setProperty('--theme-dark-accent-color', theme.theme_dark_accent_color);

            updatePrimaryPalette({
                50: theme.theme_accent_color,
                100: colors[0],
                200: colors[1],
                300: colors[2],
                400: colors[3],
                500: colors[4],
                600: colors[5],
                700: colors[6],
                800: colors[7],
                900: colors[8],
                950: colors[9]
            });
        }
    }

    public async updateTitle(title: string): Promise<any> {
        this.titleService.setTitle(`${title} | ${this._themeData.value.theme_title}`);
    }

    generateDarkerColors(hex: string, count: number = 10) {
        const darkenColor = (hex: string, percent: number) => {
            let num = parseInt(hex.slice(1), 16),
                r = num >> 16,
                g = (num >> 8) & 0x00ff,
                b = num & 0x0000ff;

            r = Math.max(0, Math.floor(r * (1 - percent)));
            g = Math.max(0, Math.floor(g * (1 - percent)));
            b = Math.max(0, Math.floor(b * (1 - percent)));

            return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
        };

        let colors = [];
        for (let i = 1; i <= count; i++) {
            colors.push(darkenColor(hex, i / (count + 1)));
        }
        return colors;
    }
}
